import {
    Component,
    createEffect,
    createMemo,
    For,
    on,
    onMount,
    Show,
} from 'solid-js'

import { useNavigate, useSearchParams } from '@solidjs/router'
import {
    api_admin_companies_count_get,
    api_admin_companies_del,
    api_admin_companies_list,
    api_admin_companies_patch,
    api_admin_companies_post,
    api_admin_companies_search_list,
    Company,
    Count,
} from 'abi'
import {
    addAlert,
    Counter,
    HoverInp,
    LoadingDots,
    LoadingElem,
    Pagination,
    SearchInp,
    setPopup,
} from 'components'
import { AddBtn } from 'components/addBtn'
import {
    CheckFillIcon,
    CloseIcon,
    CorpsIcon,
    DeleteIcon,
    EditIcon,
    WarningIcon,
} from 'icons'
import { createStore, produce } from 'solid-js/store'
import { debounce } from 'utils/tools'
import { outletRef } from '.'
import './style/corps.scss'
import { isAdmin } from './users/corp/shared'

type STATE = {
    count: Count

    page: number

    add: {
        type: 'edit' | 'add'
        show: boolean
        corp: Company | null
    }

    query: string

    loading: boolean
    corps: Company[]

    error: boolean
}

const Corps: Component = () => {
    let nav = useNavigate()

    const [state, setState] = createStore<STATE>({
        corps: [],
        page: 0,

        add: {
            show: false,
            type: 'add',
            corp: null,
        },

        query: '',

        loading: true,
        count: {
            total: -1,
            max_page: -1,
        },
        error: false,
    })

    const [search_params, setSearchParams] = useSearchParams()

    onMount(() => {
        if (!isAdmin()) return nav('/panel/403', { replace: true })

        countSearch()
    })

    createEffect(
        on(
            () => search_params.page,
            page => {
                if (!isAdmin()) return

                if (state.query) {
                    return debouncedSetSearch(state.query)
                }

                setSearchParams({ page: parseInt(page || '0') })

                if (!page) return
                defaultSearch(parseInt(page))

                return
            }
        )
    )

    const debouncedSetSearch = debounce(async (query: string) => {
        setState({ loading: true, error: false })

        let res = await api_admin_companies_search_list({
            name: `%${query}%`,
        })

        setState({ loading: false })

        if (!res.ok()) return setState({ error: true })

        if (state.query !== query) return

        setState({
            corps: res.body,
            count: {
                total: res.body.length,
                max_page: state.count.max_page,
            },
        })
    }, 200)

    const defaultSearch = async (page: number) => {
        let cres = await api_admin_companies_list({ page })
        if (cres.ok()) {
            setState({ page, corps: cres.body })
        }

        setState({ loading: false })
    }

    const countSearch = async () => {
        let res = await api_admin_companies_count_get()
        if (res.ok()) {
            setState({ count: res.body })
        }
    }

    const removeCorp = (idx: number) => {
        setState(
            produce(s => {
                let id = s.corps.findIndex(f => f.id == idx)
                if (id == -1) return
                s.corps.splice(id, 1)
            })
        )
    }

    return (
        <div class='corps-section section'>
            <div class='corps-wrapper'>
                <header class='head section_title'>
                    <CorpsIcon />
                    <span>شرکت ها</span>
                    <CorpsIcon />
                </header>

                <div
                    class='total-wrapper title_small'
                    classList={{ active: !state.loading }}
                >
                    <Counter end={state.count.total} />
                    <span>شرکت ثبت شده</span>
                </div>

                <AddBtn
                    onClick={() =>
                        setState('add', { show: true, corp: null, type: 'add' })
                    }
                    class='title_smaller'
                    title='اضافه کردن'
                />

                <form
                    class='filters-container'
                    onsubmit={e => {
                        e.preventDefault()
                        debouncedSetSearch(state.query)
                    }}
                >
                    <SearchInp
                        value={state.query}
                        active={state.query.length !== 0}
                        oninput={e =>
                            setState({ query: e.currentTarget.value.trim() })
                        }
                        onClear={() => setState({ query: '' })}
                        id='corps-search-inp'
                    />
                </form>

                <div
                    class='table-container'
                    classList={{ min: state.corps?.length == 0 }}
                >
                    <table>
                        <thead>
                            <tr class='title_small'>
                                <th>
                                    <span>نام شرکت</span>
                                </th>
                                <th class='count'>
                                    <span>تعداد اعضا</span>
                                </th>
                                <th class='date'>
                                    <span>تاریخ ثبت</span>
                                </th>
                                <th>
                                    <span>تغییر</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <Show
                                when={!state.loading}
                                fallback={
                                    <tr class='table-loading title_hero'>
                                        <td colspan='10'>
                                            <LoadingDots />
                                            <div>در حال بارگذاری...</div>
                                        </td>
                                    </tr>
                                }
                            >
                                <Show
                                    when={state.corps.length > 0}
                                    fallback={
                                        <tr class='table-empty title_hero'>
                                            <td colspan='10'>
                                                <div class='icon'>
                                                    <CloseIcon />
                                                </div>
                                                <div>
                                                    اطلاعاتی برای نمایش وجود
                                                    ندارد!
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                >
                                    <For each={state.corps}>
                                        {c => (
                                            <TableRow
                                                corp={c}
                                                page={state.page}
                                                onEdit={() =>
                                                    setState('add', {
                                                        type: 'edit',
                                                        corp: { ...c },
                                                        show: true,
                                                    })
                                                }
                                                onDel={() => removeCorp(c.id)}
                                            />
                                        )}
                                    </For>
                                </Show>
                            </Show>
                        </tbody>
                    </table>
                </div>

                <Show when={state.count.max_page > 0}>
                    <div class='pagination-container'>
                        <Pagination
                            max_page={state.count.max_page}
                            current={state.page || 0}
                            onChange={page => {
                                setSearchParams({ page })
                                outletRef?.scrollTo({
                                    top: 0,
                                    behavior: 'smooth',
                                })
                            }}
                        />
                    </div>
                </Show>
            </div>

            <AddCorpCmp
                onClose={() =>
                    setState('add', { type: 'add', corp: null, show: false })
                }
                add={state.add}
                onAdd={c => {
                    setState(
                        produce(s => {
                            s.corps.unshift(c)
                            s.count.total += 1
                        })
                    )
                }}
                onEdit={corp => {
                    setState(
                        produce(s => {
                            let id = s.corps.findIndex(c => c.id == corp.id)
                            if (id != -1) {
                                s.corps[id] = corp
                            }
                        })
                    )
                }}
            />
        </div>
    )
}

type TableRowProps = {
    corp: Company
    page: number

    onDel(): void
    onEdit(): void
}
export const TableRow: Component<TableRowProps> = P => {
    const [state, setState] = createStore({
        loading: false,
    })

    const nav = useNavigate()

    const deleteCorp = async () => {
        setState('loading', true)

        let res = await api_admin_companies_del(
            {
                id: P.corp.id,
            },
            {
                show_alert: true,
            }
        )

        setState('loading', false)

        if (!res.ok()) return

        let name = P.corp.name || 'بدون نام'

        addAlert({
            type: 'success',
            timeout: 3,
            subject: 'موفق!',
            content: `شرکت ${name} با موفقیت حذف شد.`,
        })
        P.onDel()
    }

    return (
        <tr class='title_smaller' onclick={() => nav(`corp/${P.corp.id}`)}>
            <Show
                when={!state.loading}
                fallback={
                    <td class='loading-tr' colspan={6} aria-hidden='true'>
                        <LoadingElem />
                    </td>
                }
            >
                <td>{P.corp.name || 'N / A'}</td>
            </Show>
            <td class='count'>{P.corp.manager_count + P.corp.agent_count}</td>
            <td class='date'>
                {new Date(P.corp.created_at * 1e3).toLocaleDateString('fa-IR')}
            </td>
            <td>
                <div class='ctas'>
                    <button
                        class='red icon'
                        onclick={e => {
                            e.stopPropagation()
                            setPopup({
                                show: true,
                                type: 'error',
                                timer: 2,
                                Icon: DeleteIcon,
                                title: 'شرکت را حذف میکنید؟',
                                content:
                                    'بعد از حذف شرکت نیاز به ثبت مجدد آن است.',
                                onSubmit() {
                                    deleteCorp()
                                },
                            })
                        }}
                    >
                        <DeleteIcon />
                    </button>
                    <button
                        class='yellow icon'
                        onclick={e => {
                            e.stopPropagation()

                            P.onEdit()
                        }}
                    >
                        <EditIcon />
                    </button>
                </div>
            </td>
        </tr>
    )
}

interface AddCorpCmpProps {
    add: STATE['add']

    onClose(): void

    onAdd(c: Company): void
    onEdit(c: Company): void
}
const AddCorpCmp: Component<AddCorpCmpProps> = P => {
    type STATE = {
        loading: boolean
        status: null | 'green' | 'red'

        name: string

        error: boolean

        anim: boolean
    }
    const [state, setState] = createStore<STATE>({
        loading: false,
        status: null,
        name: '',

        error: false,

        anim: false,
    })

    createEffect(() => {
        if (!P.add.show) closed()

        setState({
            name: P.add.corp?.name || '',
        })
    })

    const closed = () => {
        setState({
            loading: false,
            status: null,
            name: '',

            anim: false,
        })
    }

    const isDisable = createMemo(() => state.name.length < 3)

    const checkForm = async () => {
        if (isDisable()) {
            return setState({ error: true })
        }

        if (state.loading || state.status) return

        setState({ loading: true })

        let res

        if (P.add.type == 'edit') {
            res = await api_admin_companies_patch(
                { id: P.add.corp!.id },
                {
                    name: state.name,
                }
            )
        } else {
            res = await api_admin_companies_post(
                { name: state.name },
                { show_alert: true }
            )
        }

        setState({ loading: false })

        if (!res.ok()) return setState({ status: 'red' })

        setState({ status: 'green' })
        pop_conffeties()

        if (P.add.type == 'edit') {
            P.onEdit({
                id: P.add.corp!.id,
                name: state.name,
                created_at: P.add.corp!.created_at,
                manager_count: P.add.corp!.manager_count,
                agent_count: P.add.corp!.agent_count,
                helmet_count: P.add.corp!.helmet_count,
            })
        } else {
            P.onAdd({ ...res.body })
        }
    }

    const pop_conffeties = () => {
        // @ts-ignore
        if (typeof window.confetti !== 'function') return

        const count = 200,
            defaults = {
                origin: { y: 0.7 },
            }

        function fire(particleRatio: number, opts: any) {
            // @ts-ignore
            window.confetti(
                Object.assign({}, defaults, opts, {
                    particleCount: Math.floor(count * particleRatio),
                })
            )
        }

        fire(0.25, {
            spread: 26,
            startVelocity: 55,
        })

        fire(0.2, {
            spread: 60,
        })

        fire(0.35, {
            spread: 100,
            decay: 0.91,
            scalar: 0.8,
        })

        fire(0.1, {
            spread: 120,
            startVelocity: 25,
            decay: 0.92,
            scalar: 1.2,
        })

        fire(0.1, {
            spread: 120,
            startVelocity: 45,
        })
    }

    const OnClose = () => {
        if (state.loading) return

        if (state.status == 'red')
            return setState({ loading: false, error: false })

        P.onClose()
    }

    return (
        <div
            class='add-corp-cmp'
            classList={{ show: P.add.show }}
            onclick={OnClose}
        >
            <div
                class='add-corp-wrapper'
                onclick={e => {
                    e.stopPropagation()
                }}
            >
                <div
                    class='scaler'
                    onanimationend={() => setState({ anim: true })}
                ></div>

                <form
                    onsubmit={e => {
                        e.preventDefault()
                        checkForm()
                    }}
                    class='card'
                >
                    <button class='close icon' type='button' onclick={OnClose}>
                        <CloseIcon />
                    </button>
                    <div class='logo-wrapper'>
                        <img
                            class='logo'
                            decoding='async'
                            src='/public/imgs/logo.png'
                            alt=''
                        />
                        <div class='add-head'>
                            <p class='title_smaller sub'>اضافه شرکت در</p>
                            <p class='section_title main'>بیمه همراه</p>
                        </div>
                    </div>
                    <div class='hover-wrapper'>
                        <HoverInp
                            Icon={<CorpsIcon />}
                            holder='اسم شرکت'
                            acitve={state.name.length !== 0}
                            inpType='text'
                            onInp={e => setState({ name: e, error: false })}
                            value={state.name}
                        />
                        <div
                            class='error-wrapper title_smaller'
                            classList={{ show: state.error }}
                        >
                            <WarningIcon />
                            اسم شرکت را به درستی وارد کنید!
                        </div>
                    </div>

                    <div class='cta-wrapper title_small'>
                        <button
                            class='cta'
                            type='submit'
                            classList={{ disable: isDisable() }}
                        >
                            ثبت
                        </button>
                    </div>

                    <div
                        class='loader title_hero'
                        classList={{ show: state.loading }}
                    >
                        <LoadingDots />
                        درحال ثبت شرکت
                    </div>
                    <div
                        class='status-wrapper'
                        classList={{
                            show: !!state.status,
                            [state.status || '']: true,
                        }}
                    >
                        <Show
                            when={state.status == 'green'}
                            fallback={<CloseIcon />}
                        >
                            <CheckFillIcon />
                        </Show>

                        <p class='title_hero'>
                            <Show
                                when={state.status == 'green'}
                                fallback={
                                    'خطا در ثبت شرکت، لطفا دوباره تلاش کنید.'
                                }
                            >
                                شرکت با موفقیت ثبت شد!
                            </Show>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default Corps
